import { useState, useCallback } from 'react';
import { Download, Eye, Users } from 'react-feather';
import { Box } from '../../../components/Box';
import { Modal } from '../../../components/Modal';
import { Colors } from '../../../values/colors';
import { SuccessMessage, ErrorMessage } from '../../../utils/alerts';
import {
  getFiscalEligibility, getEligibilityUsers, downloadEligibilityUsersCsv, downloadEligibilityCsv,
} from '../../../services/Fiscal';
import {
  PageWrapper, SummaryGrid, SummaryCard, SummaryLabel, SummaryValue,
  FiltersRow, FilterGroup, FilterLabel, FilterInput, FilterToggle,
  TableWrapper, StyledTable, Th, Td, StatusBadge, ActionBtn,
  TotalRow, TotalLabel, TotalValue,
} from '../styles';

const InfoTip = ({ title }) => (
  <span
    title={title}
    style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 14, height: 14, borderRadius: '50%',
      background: '#c8cdd6', color: '#fff',
      fontSize: 9, fontWeight: 700, cursor: 'default',
      flexShrink: 0, lineHeight: 1, userSelect: 'none',
    }}
  >i</span>
);

const monthRange = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { start: iso(first), end: iso(last) };
};

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('pt-BR');
};

export const FiscalEligibility = () => {
  const range = monthRange();
  const [filterCompany, setFilterCompany] = useState('');
  const [periodStart, setPeriodStart]     = useState(range.start);
  const [periodEnd, setPeriodEnd]         = useState(range.end);
  const [searched, setSearched]           = useState(false);
  const [loading, setLoading]             = useState(false);
  const [companies, setCompanies]         = useState([]);
  const [total, setTotal]                 = useState(0);
  const [totalEligible, setTotalEligible] = useState(0);
  const [totalDependents, setTotalDependents] = useState(0);
  const [onlyWithEligible, setOnlyWithEligible] = useState(false);

  const [rawModal, setRawModal]           = useState(null); // company object
  const [rawScope, setRawScope]           = useState('single'); // 'single' | 'tree'
  const [rawUsers, setRawUsers]           = useState([]);
  const [rawLoading, setRawLoading]       = useState(false);
  const [rawPage, setRawPage]             = useState(1);
  const [rawTotal, setRawTotal]           = useState(0);
  const [exporting, setExporting]         = useState(false);
  const [exportingList, setExportingList] = useState(false);

  const RAW_PER_PAGE = 20;

  const handleSearch = async (overrides = {}) => {
    if (!periodStart || !periodEnd) {
      ErrorMessage({ message: 'Informe o período (início e fim).' });
      return;
    }
    const withEligible = overrides.withEligible ?? onlyWithEligible;

    setLoading(true);
    try {
      const data = await getFiscalEligibility({
        from: periodStart, to: periodEnd, search: filterCompany,
        onlyWithEligible: withEligible,
      });
      setCompanies(data.list || []);
      setTotal(data.total || 0);
      setTotalEligible(data.totalEligible || 0);
      setTotalDependents(data.totalDependents || 0);
      setSearched(true);
    } catch (e) {
      ErrorMessage({ message: 'Erro ao buscar elegibilidade.' });
    } finally {
      setLoading(false);
    }
  };

  const submitSearch = () => handleSearch();

  const toggleOnlyWithEligible = () => {
    const next = !onlyWithEligible;
    setOnlyWithEligible(next);
    if (searched) handleSearch({ withEligible: next });
  };

  const handleExportList = async () => {
    setExportingList(true);
    try {
      const res = await downloadEligibilityCsv({
        from: periodStart, to: periodEnd, search: filterCompany, onlyWithEligible,
      });
      const blob = new Blob([res.data], { type: 'application/octet-stream' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `elegibilidade_${periodStart}_${periodEnd}.csv`;
      link.click();
      window.URL.revokeObjectURL(link.href);
      SuccessMessage({ message: 'Exportação iniciada!' });
    } catch (e) {
      ErrorMessage({ message: 'Erro ao exportar CSV.' });
    } finally {
      setExportingList(false);
    }
  };

  const loadRawUsers = useCallback(async (company, scope, page = 1) => {
    setRawLoading(true);
    try {
      const data = await getEligibilityUsers({
        businessId: company.business_id, scope, from: periodStart, to: periodEnd,
        page, perPage: RAW_PER_PAGE,
      });
      setRawUsers(data.list || []);
      setRawTotal(data.total || 0);
      setRawPage(page);
    } catch (e) {
      ErrorMessage({ message: 'Erro ao carregar usuários.' });
      setRawUsers([]);
      setRawTotal(0);
    } finally {
      setRawLoading(false);
    }
  }, [periodStart, periodEnd]);

  const openRawModal = (company) => {
    setRawModal(company);
    setRawScope('single');
    loadRawUsers(company, 'single', 1);
  };

  const changeRawScope = (scope) => {
    setRawScope(scope);
    if (rawModal) loadRawUsers(rawModal, scope, 1);
  };

  const goToRawPage = (p) => {
    if (rawModal && p >= 1 && p <= rawPages) loadRawUsers(rawModal, rawScope, p);
  };

  const rawPages = Math.max(1, Math.ceil(rawTotal / RAW_PER_PAGE));

  const handleExportRaw = async () => {
    if (!rawModal) return;
    setExporting(true);
    try {
      const res = await downloadEligibilityUsersCsv({
        businessId: rawModal.business_id, scope: rawScope, from: periodStart, to: periodEnd,
      });
      const blob = new Blob([res.data], { type: 'application/octet-stream' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `elegibilidade_${rawModal.business_id}_${rawScope}_${periodStart}_${periodEnd}.csv`;
      link.click();
      window.URL.revokeObjectURL(link.href);
      SuccessMessage({ message: 'Exportação iniciada!' });
    } catch (e) {
      ErrorMessage({ message: 'Erro ao exportar CSV.' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <PageWrapper>
      <Box noPadding noHeader>
        <div style={{ padding: '20px 24px' }}>
          <FiltersRow>
            <FilterGroup>
              <FilterLabel>Empresa</FilterLabel>
              <FilterInput
                placeholder="Buscar empresa..."
                value={filterCompany}
                onChange={e => setFilterCompany(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && submitSearch()}
              />
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>Período — início</FilterLabel>
              <FilterInput type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} width="160px" />
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>Período — fim</FilterLabel>
              <FilterInput type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} width="160px" />
            </FilterGroup>
            <ActionBtn variant="primary" onClick={submitSearch} disabled={loading} style={{ alignSelf: 'flex-end' }}>
              {loading ? 'Pesquisando...' : 'Pesquisar'}
            </ActionBtn>
            <FilterGroup style={{ justifyContent: 'flex-end' }}>
              <FilterToggle
                active={onlyWithEligible}
                tone={Colors.successGreen}
                onClick={toggleOnlyWithEligible}
                disabled={loading}
              >
                <Users size={14} /> Somente com vidas elegíveis
              </FilterToggle>
            </FilterGroup>
          </FiltersRow>
        </div>
      </Box>

      {searched && (
        <>
          <SummaryGrid>
            <SummaryCard>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <SummaryLabel>Empresas elegíveis</SummaryLabel>
                <InfoTip title="Empresas encontradas no período filtrado" />
              </div>
              <SummaryValue>{total.toLocaleString('pt-BR')}</SummaryValue>
            </SummaryCard>
            <SummaryCard>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <SummaryLabel>Total de vidas elegíveis</SummaryLabel>
                <InfoTip title="Vidas elegíveis no período, incluindo titulares e dependentes" />
              </div>
              <SummaryValue>{totalEligible.toLocaleString('pt-BR')}</SummaryValue>
            </SummaryCard>
            <SummaryCard>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <SummaryLabel>Usuários dependentes (subtrair dos elegíveis)</SummaryLabel>
                <InfoTip title="Dependentes elegíveis no período (já incluídos no total de vidas elegíveis)" />
              </div>
              <SummaryValue>{totalDependents.toLocaleString('pt-BR')}</SummaryValue>
            </SummaryCard>
          </SummaryGrid>

          <Box noPadding noHeader>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px 0' }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: Colors.blueGray }}>Elegibilidade por empresa</span>
              <ActionBtn small variant="outline" onClick={handleExportList} disabled={exportingList || loading || total === 0}>
                <Download size={13} /> {exportingList ? 'Exportando...' : 'Exportar CSV'}
              </ActionBtn>
            </div>
            <TableWrapper style={{ margin: '12px 0 0', borderRadius: 0, border: 'none', borderTop: `1px solid ${Colors.ultraLightGray}` }}>
              <StyledTable>
                <thead>
                  <tr>
                    <Th w="60px">ID</Th>
                    <Th>Empresa</Th>
                    <Th w="150px">CNPJ</Th>
                    <Th w="120px" center>Criado em</Th>
                    <Th w="120px" center>Atualizado em</Th>
                    <Th w="80px" center>Ativo</Th>
                    <Th w="120px" right>Vidas elegíveis</Th>
                    <Th w="110px" center>Ação</Th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><Td colSpan={8} style={{ textAlign: 'center', color: Colors.mutedGray }}>Carregando...</Td></tr>
                  )}
                  {!loading && companies.map(c => {
                    const depth = c.depth ?? 0;
                    const isChild = depth > 0;
                    return (
                      <tr key={c.business_id} style={{ background: isChild ? '#fafbfc' : undefined }}>
                        <Td muted w="60px">{c.business_id}</Td>
                        <Td>
                          <span style={{ paddingLeft: Math.max(0, depth) * 20, display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                            {depth > 0 && <span style={{ color: Colors.mutedGray, fontSize: 12, flexShrink: 0 }}>{'└─'}</span>}
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.title}>{c.title}</div>
                              <div style={{ fontSize: 12, color: Colors.mutedGray, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.name}>{c.name}</div>
                            </div>
                          </span>
                        </Td>
                        <Td muted w="150px">{c.cnpj}</Td>
                        <Td center muted w="120px">{fmtDate(c.createdAt)}</Td>
                        <Td center muted w="120px">{fmtDate(c.updatedAt)}</Td>
                        <Td center w="80px">
                          <StatusBadge color={c.active ? 'successGreen' : 'red'}>{c.active ? 'Sim' : 'Não'}</StatusBadge>
                        </Td>
                        <Td right w="120px">{Number(c.eligible_users || 0).toLocaleString('pt-BR')}</Td>
                        <Td center w="110px">
                          <ActionBtn small variant="outline" onClick={() => openRawModal(c)}><Eye size={13} /> Dados</ActionBtn>
                        </Td>
                      </tr>
                    );
                  })}
                  {!loading && companies.length === 0 && (
                    <tr><Td colSpan={8} style={{ textAlign: 'center', color: Colors.mutedGray }}>Nenhuma empresa encontrada.</Td></tr>
                  )}
                </tbody>
              </StyledTable>
            </TableWrapper>
            <TotalRow style={{ padding: '12px 20px', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, color: Colors.mutedGray }}>
                {total.toLocaleString('pt-BR')} empresa{total !== 1 ? 's' : ''}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <TotalLabel>Total de vidas elegíveis no período</TotalLabel>
                <TotalValue>{totalEligible.toLocaleString('pt-BR')}</TotalValue>
              </div>
            </TotalRow>
          </Box>
        </>
      )}

      <Modal
        open={!!rawModal}
        title={`Dados brutos — ${rawModal?.title || ''}`}
        onClose={() => setRawModal(null)}
        containerStyle={{ width: '100%', maxWidth: 900, marginTop: 20, marginBottom: 20 }}
      >
        {rawModal && (
          <>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: 0, border: `1px solid ${Colors.ultraLightGray}`, borderRadius: 6, overflow: 'hidden' }}>
                {[['single', 'Somente esta empresa'], ['tree', 'Esta empresa + filhas diretas']].map(([val, label]) => (
                  <button
                    key={val}
                    onClick={() => changeRawScope(val)}
                    style={{
                      padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
                      background: rawScope === val ? Colors.primaryBlue : '#fff',
                      color: rawScope === val ? '#fff' : Colors.blueGray,
                      transition: 'background 0.15s',
                    }}
                  >{label}</button>
                ))}
              </div>
              <ActionBtn small variant="outline" onClick={handleExportRaw} disabled={exporting || rawLoading}>
                <Download size={13} /> {exporting ? 'Exportando...' : 'Exportar CSV'}
              </ActionBtn>
            </div>
            <TableWrapper>
              <StyledTable style={{ minWidth: 1080 }}>
                <thead>
                  <tr>
                    <Th w="80px" center>ID Empresa</Th>
                    <Th w="150px">Empresa</Th>
                    <Th w="150px">Nome</Th>
                    <Th w="190px">Email</Th>
                    <Th w="120px">Telefone</Th>
                    <Th w="120px">CPF</Th>
                    <Th w="70px" center>ID Pai</Th>
                    <Th w="100px" center>Criado em</Th>
                    <Th w="100px" center>Deletado em</Th>
                    <Th w="100px" center>Dependente</Th>
                    <Th w="110px" center>Elegibilidade</Th>
                  </tr>
                </thead>
                <tbody>
                  {rawLoading && (
                    <tr><Td colSpan={11} style={{ textAlign: 'center', color: Colors.mutedGray }}>Carregando...</Td></tr>
                  )}
                  {!rawLoading && rawUsers.map(u => {
                    const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis' };
                    return (
                    <tr key={u.id}>
                      <Td muted center w="80px">{u.business_id}</Td>
                      <Td w="150px" style={ellipsis} title={u.company}>{u.company}</Td>
                      <Td w="150px" style={ellipsis} title={u.name}>{u.name}</Td>
                      <Td muted w="190px" style={ellipsis} title={u.email}>{u.email}</Td>
                      <Td muted w="120px">{u.phone}</Td>
                      <Td muted w="120px">{u.cpf}</Td>
                      <Td center muted w="70px">{u.parent_id ?? '—'}</Td>
                      <Td center muted w="100px">{fmtDate(u.createdAt)}</Td>
                      <Td center muted w="100px">{u.deletedAt ? fmtDate(u.deletedAt) : '—'}</Td>
                      <Td center w="100px">
                        <StatusBadge color={Number(u.is_dependent) ? 'primaryBlue' : 'lightGray2'}>
                          {Number(u.is_dependent) ? 'Sim' : 'Não'}
                        </StatusBadge>
                      </Td>
                      <Td center w="110px">
                        <StatusBadge color={Number(u.eligible) ? 'successGreen' : 'red'}>
                          {Number(u.eligible) ? 'Elegível' : 'Inelegível'}
                        </StatusBadge>
                      </Td>
                    </tr>
                    );
                  })}
                  {!rawLoading && rawUsers.length === 0 && (
                    <tr><Td colSpan={11} style={{ textAlign: 'center', color: Colors.mutedGray }}>Nenhum usuário encontrado.</Td></tr>
                  )}
                </tbody>
              </StyledTable>
            </TableWrapper>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
              <span style={{ fontSize: 13, color: Colors.mutedGray }}>
                {rawTotal.toLocaleString('pt-BR')} usuário{rawTotal !== 1 ? 's' : ''}
              </span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: Colors.mutedGray }}>Página {rawPage} de {rawPages}</span>
                <ActionBtn small onClick={() => goToRawPage(rawPage - 1)} disabled={rawPage === 1 || rawLoading}>‹</ActionBtn>
                <ActionBtn small onClick={() => goToRawPage(rawPage + 1)} disabled={rawPage === rawPages || rawLoading}>›</ActionBtn>
              </div>
            </div>
          </>
        )}
      </Modal>
    </PageWrapper>
  );
};
